export interface PerformanceMetric {
  operation: string;
  duration: number;
  timestamp: number;
  success: boolean;
  metadata?: Record<string, any>;
}

interface ErrorMetadata {
  component?: string;
  operation?: string;
  [key: string]: string | number | boolean | undefined;
}

interface EventMetadata {
  category: string;
  action: string;
  label?: string;
  value?: number;
  [key: string]: string | number | undefined;
}

interface PerformanceMetadata {
  browser?: string;
  device?: string;
  connection?: string;
  [key: string]: string | number | undefined;
}

interface ApiCallMetadata {
  method?: string;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  [key: string]: string | Record<string, string> | undefined;
}

interface SessionMetadata {
  userAgent?: string;
  location?: string;
  referrer?: string;
  [key: string]: string | undefined;
}

interface ImageOperationMetadata {
  imageId?: string;
  size?: number;
  format?: string;
  [key: string]: string | number | undefined;
}

class MonitoringService {
  private metrics: PerformanceMetric[] = [];
  private readonly MAX_METRICS = 100;

  // 记录性能指标
  trackPerformance(metric: PerformanceMetric) {
    this.metrics.push(metric);
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics.shift();
    }
    
    // 如果超过性能目标，记录警告
    if (metric.duration > this.getThreshold(metric.operation)) {
      console.warn(`Performance warning: ${metric.operation} took ${metric.duration}ms`);
    }
  }

  // 获取操作的性能阈值
  private getThreshold(operation: string): number {
    const thresholds: Record<string, number> = {
      'image_generation': 10000, // 10s
      'credit_check': 2000,    // 2s
      'image_download': 3000,  // 3s
      'image_share': 1000,    // 1s
    };
    return thresholds[operation] || 5000;
  }

  // 错误跟踪
  trackError(error: Error, metadata: ErrorMetadata = {}) {
    console.error('Error:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...metadata,
    });

    // 如果使用了错误跟踪服务（如 Sentry），在这里添加相关代码
  }

  // 获取性能报告
  getPerformanceReport(): PerformanceMetric[] {
    return this.metrics;
  }

  // 跟踪用户事件
  trackEvent(metadata: EventMetadata) {
    console.log('Event:', metadata);

    // 如果使用了分析服务（如 Google Analytics），在这里添加相关代码
  }

  // 跟踪 API 调用
  trackApiCall(endpoint: string, duration: number, status: number, metadata: ApiCallMetadata = {}) {
    console.log('API Call:', {
      endpoint,
      duration,
      status,
      ...metadata,
    });

    // 如果使用了 API 监控服务，在这里添加相关代码
  }

  // 跟踪用户会话
  trackSession(userId: string, metadata: SessionMetadata = {}) {
    console.log('Session:', {
      userId,
      timestamp: new Date().toISOString(),
      ...metadata,
    });

    // 如果使用了会话跟踪服务，在这里添加相关代码
  }

  // 跟踪图片操作
  trackImageOperation(operation: 'download' | 'share', platform: string, metadata: ImageOperationMetadata = {}) {
    console.log('Image Operation:', {
      operation,
      platform,
      timestamp: new Date().toISOString(),
      ...metadata,
    });

    // 如果使用了分析服务，在这里添加相关代码
  }
}

export const monitoring = new MonitoringService(); 